// src/pages/Invoices.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { apiRequest } from "../api";
import { ui, uiHandlers } from "../uiStyles";

export default function Invoices() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [clients, setClients] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [invoicePayments, setInvoicePayments] = useState(null);
  const [filterClientId, setFilterClientId] = useState("");
  const [search, setSearch] = useState("");
  const [showArchived, setShowArchived] = useState(false);
  const [archivingId, setArchivingId] = useState(null);
  const [showNewInvoiceSetup, setShowNewInvoiceSetup] = useState(false);
  const [selectedInvoiceClientId, setSelectedInvoiceClientId] = useState("");
  const [selectedInvoiceJobId, setSelectedInvoiceJobId] = useState("");
  const [connectReturnMessage, setConnectReturnMessage] = useState("");

  async function load() {
    setLoading(true);
    setErr("");

    try {
      const [cJson, archivedClients, iJson, jJson, connectJson] = await Promise.all([
        apiRequest("/clients"),
        apiRequest("/clients?archived=true").catch(() => []),
        apiRequest(showArchived ? "/invoices?archived=true" : "/invoices"),
        apiRequest("/jobs/"),
        apiRequest("/billing/connect/status").catch(() => null),
      ]);

      setClients([
        ...(Array.isArray(cJson) ? cJson : []),
        ...(Array.isArray(archivedClients) ? archivedClients : []),
      ]);
      setInvoices(Array.isArray(iJson) ? iJson : []);
      setJobs(Array.isArray(jJson) ? jJson : []);
      setInvoicePayments(connectJson);
    } catch (e) {
      setErr(String(e?.message || e || "Failed to load."));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, [showArchived]);

  useEffect(() => {
    const connectReturn = searchParams.get("stripe_connect");

    if (connectReturn === "return") {
      setConnectReturnMessage(
        "Stripe onboarding was completed. JobTelesis is refreshing your Invoice Payments status."
      );
      load().then(() => {
        setConnectReturnMessage(
          "Stripe onboarding complete. Invoice Payments status has been refreshed."
        );
      });
    }

    if (connectReturn === "refresh") {
      setConnectReturnMessage(
        "Stripe needs more information before Invoice Payments can be activated. Please continue onboarding when ready."
      );
      load();
    }
  }, [searchParams]);

  const filteredInvoiceJobs = selectedInvoiceClientId
    ? jobs.filter((job) => String(job.client_id) === String(selectedInvoiceClientId))
    : jobs;

  function handleNewInvoice() {
    setShowNewInvoiceSetup((v) => !v);
    setErr("");
  }

  function continueToNewInvoice() {
    if (!selectedInvoiceClientId) {
      setErr("Select a client before creating an invoice.");
      return;
    }

    if (!selectedInvoiceJobId) {
      setErr("Select a job before creating an invoice.");
      return;
    }

    navigate(`/invoices/new?job_id=${selectedInvoiceJobId}`);
  }

  const filtered = useMemo(() => {
    let rows = invoices;

    if (filterClientId) {
      rows = rows.filter((r) => String(r.client_id) === String(filterClientId));
    }

    if (search.trim()) {
      const q = search.trim().toLowerCase();
      rows = rows.filter((r) => {
        const client = clients.find((item) => String(item.id) === String(r.client_id)) || {};
        const name = client.company_name || client.contact_name || "";
        return (
          (r.invoice_number || "").toLowerCase().includes(q) ||
          name.toLowerCase().includes(q)
        );
      });
    }

    return rows;
  }, [invoices, clients, filterClientId, search]);

  async function setInvoiceArchived(event, id, next) {
    event.preventDefault();
    event.stopPropagation();
    setArchivingId(id);
    setErr("");
    try {
      await apiRequest(`/invoices/${id}/archive`, {
        method: "PATCH",
        body: { archived: next },
      });
      setInvoices((current) => current.filter((row) => row.id !== id));
    } catch (e) {
      setErr(String(e?.message || e || "Could not update this invoice."));
    } finally {
      setArchivingId(null);
    }
  }

  const money = (v) =>
    Number(v || 0).toLocaleString(undefined, {
      style: "currency",
      currency: "USD",
    });

  const invoicePaymentsActive = !!(
    invoicePayments?.stripe_connect_onboarding_complete &&
    invoicePayments?.stripe_connect_charges_enabled &&
    invoicePayments?.stripe_connect_payouts_enabled
  );

  const invoicePaymentsPending = !!(
    invoicePayments?.stripe_connect_account_id && !invoicePaymentsActive
  );

  const invoicePaymentsStatus = invoicePaymentsActive
    ? "Active"
    : invoicePaymentsPending
    ? "Onboarding Pending"
    : "Not Enabled";

  const invoicePaymentsStatusColor = invoicePaymentsActive
    ? "#22c55e"
    : invoicePaymentsPending
    ? "#f5c542"
    : "#ef4444";

  async function activateInvoicePayments() {
    setErr("");

    const ok = window.confirm(
      "Activate Invoice Payments?\n\n" +
        "JobTelesis Transaction Fees: 0% for the first 90 days after activation.\n" +
        "Beginning on day 91: 1% JobTelesis Transaction Fee, maximum $50.00 per transaction.\n\n" +
        "Standard Stripe processing fees apply separately.\n\n" +
        "Click OK to accept the JobTelesis Invoice Payments Agreement and continue to Stripe onboarding."
    );

    if (!ok) return;

    try {
      await apiRequest("/billing/connect/accept-agreement", {
        method: "POST",
      });

      const json = await apiRequest("/billing/connect/onboarding-link", {
        method: "POST",
      });

      if (!json?.onboarding_url) {
        throw new Error("Stripe onboarding link was not returned.");
      }

      window.location.href = json.onboarding_url;
    } catch (e) {
      setErr(String(e?.message || e || "Failed to activate Invoice Payments."));
    }
  }

  return (
    <div style={ui.page}>
      <div style={{ marginBottom: 20 }}>
        <h2 style={{ margin: 0, color: "#ffffff", fontSize: 24 }}>
          Invoices
        </h2>
        <p style={{ marginTop: 8, color: "#9fb0c3", fontSize: 14 }}>
          Track invoices, payments, balances, and client billing status.
        </p>
      </div>

      {connectReturnMessage && (
        <div
          style={{
            ...ui.card,
            border: "1px solid rgba(34,197,94,0.75)",
            background:
              "linear-gradient(135deg, rgba(34,197,94,0.16), rgba(16,38,58,0.98))",
            marginBottom: 18,
          }}
        >
          <h3 style={{ margin: "0 0 8px", color: "#bbf7d0" }}>
            Invoice Payments Update
          </h3>
          <p style={{ margin: 0, color: "#dbeafe" }}>{connectReturnMessage}</p>
        </div>
      )}

      <div
        style={{
          ...ui.card,
          border: "1px solid rgba(245,197,66,0.75)",
          background:
            "linear-gradient(135deg, rgba(245,197,66,0.13), rgba(16,38,58,0.98))",
        }}
      >
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "minmax(0, 1fr) minmax(260px, 0.85fr)",
            gap: 24,
            alignItems: "center",
          }}
        >
          <div>
            <h3 style={{ margin: "0 0 10px", color: "#f5c542", fontSize: 20 }}>
              Invoice Payments
            </h3>

            <p style={{ color: "#ffffff", marginTop: 0, marginBottom: 14, fontSize: 14 }}>
              Accept online payments directly through JobTelesis.
            </p>

            <div style={{ color: "#ffffff", fontWeight: 800, marginBottom: 4 }}>
              Status:
            </div>

            <div
              style={{
                color: invoicePaymentsStatusColor,
                fontSize: 18,
                fontWeight: 900,
                marginBottom: 16,
              }}
            >
              {invoicePaymentsStatus}
            </div>

            {!invoicePaymentsActive ? (
              <button
                type="button"
                onClick={activateInvoicePayments}
                style={{ ...ui.btnPrimary, fontSize: 14, padding: "9px 14px" }}
                {...uiHandlers.primaryHover}
              >
                Activate Invoice Payments
              </button>
            ) : (
              <button
                type="button"
                style={{ ...ui.btnSecondary, fontSize: 15, padding: "11px 18px" }}
              >
                Manage Invoice Payments
              </button>
            )}
          </div>

          <div
            style={{
              borderLeft: "1px solid rgba(255,255,255,0.14)",
              paddingLeft: 22,
              display: "grid",
              gap: 14,
            }}
          >
            {[
              "Customers can pay invoices online.",
              "Payments are automatically recorded.",
              "Receipts are automatically generated.",
            ].map((line) => (
              <div key={line} style={{ display: "flex", gap: 10, alignItems: "center" }}>
                <span
                  style={{
                    width: 22,
                    height: 22,
                    borderRadius: "50%",
                    border: "2px solid #f5c542",
                    color: "#f5c542",
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontWeight: 900,
                    flex: "0 0 auto",
                  }}
                >
                  ✓
                </span>
                <span style={{ color: "#ffffff", fontSize: 14 }}>{line}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div style={ui.card}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 14,
            flexWrap: "wrap",
            marginBottom: 18,
          }}
        >
          <div>
            <h3 style={{ margin: 0, color: "#ffffff", fontSize: 20 }}>
              Invoice Workspace
            </h3>
            <div style={{ color: "#9fb0c3", fontSize: 13, marginTop: 6 }}>
              {loading ? "Loading invoices…" : `${filtered.length} invoices shown`}
            </div>
          </div>

          <button
            type="button"
            onClick={handleNewInvoice}
            style={ui.btnPrimary}
            {...uiHandlers.primaryHover}
          >
            {showNewInvoiceSetup ? "Hide New Invoice" : "+ New Invoice"}
          </button>
        </div>

        {showNewInvoiceSetup && (
          <div
            style={{
              ...ui.subCard,
              marginBottom: 16,
              border: "1px solid rgba(0,194,255,0.28)",
              background:
                "linear-gradient(135deg, rgba(0,194,255,0.10), rgba(16,38,58,0.96))",
            }}
          >
            <div
              style={{
                color: "#00c2ff",
                fontSize: 12,
                fontWeight: 900,
                textTransform: "uppercase",
                letterSpacing: 1,
                marginBottom: 6,
              }}
            >
              Invoice Setup
            </div>

            <h3 style={{ marginTop: 0, marginBottom: 8, color: "#ffffff" }}>
              Select a client and job before creating an invoice.
            </h3>

            <p style={{ color: "#c9d8e8", marginTop: 0, marginBottom: 16, lineHeight: 1.55 }}>
              Invoices must be connected to a JOB so invoice, payment, receipt, and communication history stays organized under the correct work file.
            </p>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                gap: 12,
                marginBottom: 14,
              }}
            >
              <label style={{ display: "grid", gap: 6 }}>
                <span style={{ color: "#dbe7f3", fontSize: 13, fontWeight: 700 }}>
                  Client
                </span>
                <select
                  value={selectedInvoiceClientId}
                  onChange={(e) => {
                    setSelectedInvoiceClientId(e.target.value);
                    setSelectedInvoiceJobId("");
                  }}
                  style={ui.input}
                >
                  <option value="">Select Client</option>
                  {clients.filter((client) => !client.archived).map((client) => (
                    <option key={client.id} value={client.id}>
                      {client.company_name || client.contact_name || `Client #${client.id}`}
                    </option>
                  ))}
                </select>
              </label>

              <label style={{ display: "grid", gap: 6 }}>
                <span style={{ color: "#dbe7f3", fontSize: 13, fontWeight: 700 }}>
                  Job
                </span>
                <select
                  value={selectedInvoiceJobId}
                  onChange={(e) => setSelectedInvoiceJobId(e.target.value)}
                  style={ui.input}
                  disabled={!selectedInvoiceClientId}
                >
                  <option value="">
                    {selectedInvoiceClientId ? "Select Job" : "Select a client first"}
                  </option>
                  {filteredInvoiceJobs.map((job) => (
                    <option key={job.id} value={job.id}>
                      {`JOB ID #${job.id} — ${job.defendant_name || job.court_name || job.job_number || "Untitled Job"}`}
                    </option>
                  ))}
                </select>
              </label>
            </div>

            <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
              <button
                type="button"
                onClick={continueToNewInvoice}
                style={ui.btnPrimary}
                {...uiHandlers.primaryHover}
              >
                Continue to Invoice
              </button>

              <button
                type="button"
                onClick={() => {
                  setShowNewInvoiceSetup(false);
                  setSelectedInvoiceClientId("");
                  setSelectedInvoiceJobId("");
                  setErr("");
                }}
                style={ui.btnSecondary}
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        <div
          style={{
            display: "flex",
            gap: 10,
            flexWrap: "wrap",
            marginBottom: 18,
          }}
        >
          <select
            value={filterClientId}
            onChange={(e) => setFilterClientId(e.target.value)}
            style={ui.input}
          >
            <option value="">All clients</option>
            {clients.map((c) => (
              <option key={c.id} value={c.id}>
                {c.company_name || c.contact_name || `Client #${c.id}`}
              </option>
            ))}
          </select>

          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search invoice # or client"
            style={ui.input}
          />
          <label style={{ display: "flex", alignItems: "center", gap: 8, color: "#dbe7f3" }}>
            <input
              type="checkbox"
              checked={showArchived}
              onChange={(event) => setShowArchived(event.target.checked)}
            />
            Archived
          </label>
        </div>

        {err && (
          <div
            style={{
              ...ui.subCard,
              color: "#fecaca",
              border: "1px solid rgba(239,68,68,0.35)",
              background: "rgba(127,29,29,0.28)",
              marginBottom: 16,
            }}
          >
            {err}
          </div>
        )}

        {loading ? (
          <div style={{ color: "#9fb0c3" }}>Loading…</div>
        ) : filtered.length === 0 ? (
          <div style={{ ...ui.subCard, color: "#9fb0c3" }}>
            No invoices found.
          </div>
        ) : (
          <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: 14 }}>
            {filtered.map((inv) => {
              const client = clients.find((c) => String(c.id) === String(inv.client_id)) || {};
              const paid = Number(inv.amount_paid || 0);
              const total = Number(inv.total || 0);
              const balance = total - paid;
              const status = String(inv.status || "—").toUpperCase();

              return (
                <div
                  key={inv.id}
                  onClick={() => navigate(`/invoices/${inv.id}`)}
                  style={{
                    ...ui.subCard,
                    cursor: "pointer",
                    transition: "all 0.18s ease",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      gap: 12,
                      flexWrap: "wrap",
                      marginBottom: 14,
                    }}
                  >
                    <div>
                      <div style={{ color: "#ffffff", fontWeight: 800, fontSize: 17 }}>
                        {inv.invoice_number || `#${inv.id}`}
                      </div>
                      <div
                        style={{
                          display: "inline-flex",
                          marginTop: 8,
                          padding: "6px 12px",
                          borderRadius: 8,
                          background: "#f5c542",
                          color: "#062033",
                          fontWeight: 900,
                        }}
                      >
                        {client.company_name ||
                          client.contact_name ||
                          `Client #${inv.client_id || "-"}`}
                      </div>
                    </div>

                    <span
                      style={{
                        border: "1px solid rgba(255,255,255,0.12)",
                        borderRadius: 999,
                        padding: "5px 10px",
                        color: status === "PAID" || status === "SENT" ? "#22c55e" : "#f5c542",
                        fontSize: 12,
                        fontWeight: 800,
                        height: "fit-content",
                      }}
                    >
                      {status}
                    </span>
                  </div>

                  <div className="jt-invoice-meta-grid">
                    <Mini label="Date" value={inv.issue_date || "—"} />
                    <Mini label="Total" value={money(total)} gold />
                    <Mini label="Paid" value={money(paid)} />
                    <Mini label="Balance" value={money(balance)} gold={balance > 0} />
                  </div>
                  <div style={{ display: "flex", gap: 10, marginTop: 14 }} onClick={(event) => event.stopPropagation()}>
                    <button
                      type="button"
                      disabled={archivingId === inv.id}
                      onClick={(event) => setInvoiceArchived(event, inv.id, !showArchived)}
                      style={{
                        ...ui.btnSecondary,
                        borderColor: "rgba(239,68,68,0.55)",
                        color: "#fca5a5",
                        boxShadow: "none",
                      }}
                    >
                      {archivingId === inv.id ? "Saving…" : showArchived ? "Unarchive" : "Archive"}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

function Mini({ label, value, gold }) {
  return (
    <div
      style={{
        border: "1px solid rgba(255,255,255,0.06)",
        borderRadius: 12,
        padding: 12,
        background: "#0b2239",
        minWidth: 0,
      }}
    >
      <div
        style={{
          color: "#9fb0c3",
          fontSize: 11,
          textTransform: "uppercase",
          marginBottom: 6,
        }}
      >
        {label}
      </div>
      <div
        style={{
          color: gold ? "#f5c542" : "#ffffff",
          fontWeight: 800,
          overflowWrap: "anywhere",
        }}
      >
        {value}
      </div>
    </div>
  );
}
